import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableUserSelect from "@/components/SearchableUserSelect";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Plus, CalendarCheck, CreditCard, X, CaretLeft, CaretRight, Bank, CheckCircle, Trash, WhatsappLogo, UploadSimple, Eye, Broadcast, UsersFour, PencilSimple } from "@phosphor-icons/react";
import EventMediaGallery from "@/components/EventMediaGallery";

const statusColors = {
  reservada: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  confirmada: "bg-green-500/20 text-green-400 border-green-500/30",
  cancelada: "bg-red-500/20 text-red-400 border-red-500/30",
};
const paymentColors = {
  pendiente: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  en_revision: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  pagada: "bg-green-500/20 text-green-400 border-green-500/30",
};
const TIME_SLOTS = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00"];

export default function CourtRentals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rentals, setRentals] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ club_id: "", court_name: "", date: "", time_slot: "" });
  const [selectedClub, setSelectedClub] = useState(null);
  const [paymentChecking, setPaymentChecking] = useState(false);
  const [calendarClub, setCalendarClub] = useState("");
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [availability, setAvailability] = useState({});
  const [viewMode, setViewMode] = useState("list");
  const [prices, setPrices] = useState({ "1h": 0, "1.5h": 0, "2h": 0 });
  const [editingPrices, setEditingPrices] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);
  const [allPlayers, setAllPlayers] = useState([]);
  const [playersDialog, setPlayersDialog] = useState(null);
  const [playersForm, setPlayersForm] = useState([{}, {}, {}, {}]);
  const [adminEditDialog, setAdminEditDialog] = useState(null);
  const [adminEditForm, setAdminEditForm] = useState({ amount: 0, transfer_info: "", date: "", time_slot: "", court_name: "" });
  const canManagePrices = user?.role === "admin" || user?.role === "directivo";
  const isAdminOrDir = canManagePrices;

  const loadRentals = useCallback(() => {
    api.get("/court-rentals").then(({ data }) => setRentals(data)).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([api.get("/court-rentals"), api.get("/clubs"), api.get("/court-rentals/prices"), api.get("/users?role=jugador")])
      .then(([r, c, p, u]) => { setRentals(r.data); setClubs(c.data); setPrices(p.data); setAllPlayers(u.data || []); if (c.data.length > 0) setCalendarClub(c.data[0].id); })
      .catch(() => toast.error("Error al cargar"))
      .finally(() => setLoading(false));
  }, []);

  const savePrices = async () => {
    setSavingPrices(true);
    try {
      const { data } = await api.put("/admin/rental-prices", {
        "1h": parseFloat(prices["1h"]) || 0,
        "1.5h": parseFloat(prices["1.5h"]) || 0,
        "2h": parseFloat(prices["2h"]) || 0,
      });
      setPrices(data);
      setEditingPrices(false);
      toast.success("Precios actualizados");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar precios");
    } finally {
      setSavingPrices(false);
    }
  };

  // Load availability when calendar club changes
  useEffect(() => {
    if (!calendarClub) return;
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
    const end = new Date(today.getFullYear(), today.getMonth() + 2, 0).toISOString().slice(0, 10);
    api.get(`/court-rentals/availability?club_id=${calendarClub}&start_date=${start}&end_date=${end}`)
      .then(({ data }) => setAvailability(data.booked || {}))
      .catch(() => {});
  }, [calendarClub]);

  // Pre-fill reservation from query params (comes from ClubDetail "Reservar")
  useEffect(() => {
    const preClub = searchParams.get("club_id");
    const preCourt = searchParams.get("court");
    const preDate = searchParams.get("date");
    if (preClub && clubs.length > 0) {
      const clubObj = clubs.find((c) => c.id === preClub);
      if (clubObj) {
        setForm({ club_id: preClub, court_name: preCourt || "", date: preDate || "", time_slot: "" });
        setSelectedClub(clubObj);
        setDialogOpen(true);
        // clean query params so dialog doesn't reopen on re-render
        searchParams.delete("club_id");
        searchParams.delete("court");
        searchParams.delete("date");
        setSearchParams(searchParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubs]);

  // Poll payment
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    setPaymentChecking(true);
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 5) { setPaymentChecking(false); return; }
      try {
        const { data } = await api.get(`/court-rentals/payment-status/${sessionId}`);
        if (data.payment_status === "paid") { toast.success("Pago exitoso!"); setPaymentChecking(false); setSearchParams({}); loadRentals(); return; }
      } catch { /* ignore */ }
      attempts++;
      setTimeout(poll, 2000);
    };
    poll();
  }, [searchParams, setSearchParams, loadRentals]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/court-rentals", form);
      toast.success("Cancha reservada!");
      setDialogOpen(false);
      loadRentals();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handlePay = async (rentalId) => {
    try {
      const { data } = await api.post(`/court-rentals/${rentalId}/pay`, { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancelar esta reserva?")) return;
    try { await api.delete(`/court-rentals/${id}`); toast.success("Cancelada"); loadRentals(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleConfirmPayment = async (id) => {
    if (!window.confirm("Confirmar pago de esta reserva?")) return;
    try { await api.post(`/payments/confirm/${id}`); toast.success("Pago confirmado!"); loadRentals(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleAdminDelete = async (id) => {
    if (!window.confirm("Eliminar esta reserva permanentemente?")) return;
    try { await api.delete(`/admin/rentals/${id}`); toast.success("Reserva eliminada"); loadRentals(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleUploadProof = async (rentalId) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,.pdf";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post(`/court-rentals/${rentalId}/upload-proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Comprobante subido! El admin revisara tu pago.");
        loadRentals();
      } catch (err) { toast.error(err.response?.data?.detail || "Error al subir"); }
    };
    input.click();
  };

  const viewProof = (rentalId) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/court-rentals/${rentalId}/proof`, "_blank");
  };

  // ===== 4-PLAYER ROSTER =====
  const openPlayersDialog = (rental) => {
    const existing = rental.players || [];
    const form = [0, 1, 2, 3].map((i) => {
      const p = existing[i];
      return p ? { user_id: p.user_id || "", name: p.name, is_guest: !!p.is_guest } : {};
    });
    // Ensure owner is slot 1 by default if empty
    if (!existing.length) {
      form[0] = { user_id: rental.user_id, name: rental.user_name, is_guest: false };
    }
    setPlayersForm(form);
    setPlayersDialog(rental);
  };

  const savePlayers = async () => {
    if (!playersDialog) return;
    const cleaned = playersForm.map((p) => ({
      user_id: p.user_id || null,
      name: (p.name || "").trim(),
      is_guest: !p.user_id,
    }));
    if (cleaned.some((p) => !p.name)) { toast.error("Llena los 4 jugadores"); return; }
    try {
      await api.post(`/court-rentals/${playersDialog.id}/players`, { players: cleaned });
      toast.success("Jugadores guardados");
      setPlayersDialog(null);
      loadRentals();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const confirmRental = async (id) => {
    try { await api.post(`/court-rentals/${id}/confirm`); toast.success("Reserva confirmada"); loadRentals(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openAdminEdit = (r) => {
    setAdminEditForm({
      amount: r.amount || 0,
      transfer_info: r.transfer_info || "",
      date: r.date || "",
      time_slot: r.time_slot || "",
      court_name: r.court_name || "",
    });
    setAdminEditDialog(r);
  };
  const saveAdminEdit = async () => {
    if (!adminEditDialog) return;
    try {
      await api.put(`/court-rentals/admin/${adminEditDialog.id}`, {
        amount: parseFloat(adminEditForm.amount) || 0,
        transfer_info: adminEditForm.transfer_info,
        date: adminEditForm.date || null,
        time_slot: adminEditForm.time_slot || null,
        court_name: adminEditForm.court_name || null,
      });
      toast.success("Reserva actualizada");
      setAdminEditDialog(null);
      loadRentals();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const launchMatchFromRental = async (r) => {
    const pl = r.players || [];
    if (pl.length !== 4) { toast.error("Primero define los 4 jugadores"); return; }
    try {
      const { data } = await api.post("/matches", {
        pair1_p1: { id: pl[0].user_id, name: pl[0].name },
        pair1_p2: { id: pl[1].user_id, name: pl[1].name },
        pair2_p1: { id: pl[2].user_id, name: pl[2].name },
        pair2_p2: { id: pl[3].user_id, name: pl[3].name },
        rental_id: r.id,
        club_id: r.club_id || null,
        date: r.date,
        time: r.time_slot || "",
        format: "standard",
      });
      toast.success("Marcador iniciado");
      navigate(`/dashboard/marcador/${data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const update = (f, v) => {
    setForm((p) => ({ ...p, [f]: v }));
    if (f === "club_id") {
      const club = clubs.find((c) => c.id === v);
      setSelectedClub(club);
      setForm((p) => ({ ...p, club_id: v, court_name: "" }));
    }
  };

  const getDateBookings = (date) => {
    const dateStr = date.toISOString().slice(0, 10);
    return availability[dateStr] || [];
  };

  const selectedDateStr = calendarDate?.toISOString().slice(0, 10);
  const selectedDateBookings = availability[selectedDateStr] || [];
  const selectedClubObj = clubs.find(c => c.id === calendarClub);
  const totalSlots = (selectedClubObj?.courts_count || 1) * TIME_SLOTS.length;
  const bookedSlots = selectedDateBookings.length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="court-rentals-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Reservas</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Canchas</h1>
        </div>
        <div className="flex gap-2">
          <Button variant={viewMode === "calendar" ? "default" : "outline"} onClick={() => setViewMode("calendar")}
            className={viewMode === "calendar" ? "bg-[#CCFF00] text-black font-bold uppercase text-xs" : "border-white/20 text-gray-300 text-xs uppercase font-bold"} data-testid="view-calendar-btn">
            <CalendarCheck size={16} className="mr-1" /> Calendario
          </Button>
          <Button variant={viewMode === "list" ? "default" : "outline"} onClick={() => setViewMode("list")}
            className={viewMode === "list" ? "bg-[#CCFF00] text-black font-bold uppercase text-xs" : "border-white/20 text-gray-300 text-xs uppercase font-bold"} data-testid="view-list-btn">
            Lista
          </Button>
          <Button onClick={() => { setForm({ club_id: "", court_name: "", date: "", time_slot: "" }); setSelectedClub(null); setDialogOpen(true); }}
            className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs" data-testid="create-rental-btn">
            <Plus size={16} className="mr-1" /> Reservar
          </Button>
        </div>
      </div>

      {paymentChecking && (
        <div className="card-tactical p-6 text-center border-[#CCFF00]/30">
          <div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin mx-auto mb-3" />
          <p className="text-[#CCFF00] font-semibold">Verificando pago...</p>
        </div>
      )}

      {/* Rental Prices Card */}
      <div className="card-tactical p-5" data-testid="rental-prices-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Tarifas</p>
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white">Precios de Renta</h3>
          </div>
          {canManagePrices && !editingPrices && (
            <Button size="sm" variant="outline" onClick={() => setEditingPrices(true)} className="border-white/20 text-gray-300 hover:text-[#CCFF00] hover:border-[#CCFF00]/30 text-xs uppercase font-bold" data-testid="edit-prices-btn">
              Editar
            </Button>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {["1h", "1.5h", "2h"].map((k) => (
            <div key={k} className="bg-[#0A0A0A] border border-white/10 p-3">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">{k === "1h" ? "1 hora" : k === "1.5h" ? "1.5 horas" : "2 horas"}</p>
              {editingPrices ? (
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={prices[k]}
                  onChange={(e) => setPrices((p) => ({ ...p, [k]: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/20 text-white h-9 focus:border-[#CCFF00]"
                  data-testid={`price-input-${k.replace(".", "-")}`}
                />
              ) : (
                <p className="font-['JetBrains_Mono'] text-lg text-white">${Number(prices[k] || 0).toFixed(2)} MXN</p>
              )}
            </div>
          ))}
        </div>
        {editingPrices && (
          <div className="flex gap-2 mt-4 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setEditingPrices(false)} className="text-gray-400 text-xs uppercase font-bold" data-testid="cancel-prices-btn">Cancelar</Button>
            <Button size="sm" onClick={savePrices} disabled={savingPrices} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs" data-testid="save-prices-btn">
              {savingPrices ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        )}
      </div>

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="calendar-view">
          <div className="lg:col-span-1 card-tactical p-4">
            <div className="mb-4">
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Club</Label>
              <Select value={calendarClub} onValueChange={setCalendarClub}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="calendar-club-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">{clubs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Calendar
              mode="single"
              selected={calendarDate}
              onSelect={(d) => d && setCalendarDate(d)}
              className="bg-transparent text-white"
              modifiers={{
                booked: (d) => { const ds = d.toISOString().slice(0,10); return !!(availability[ds]?.length); }
              }}
              modifiersClassNames={{ booked: "!bg-[#CCFF00]/20 !text-[#CCFF00] font-bold" }}
              data-testid="court-calendar"
            />
          </div>
          <div className="lg:col-span-2 card-tactical p-6" data-testid="day-detail">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">{selectedClubObj?.name}</p>
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase">{selectedDateStr}</h3>
              </div>
              <span className="font-['JetBrains_Mono'] text-sm text-gray-400">{bookedSlots}/{totalSlots} ocupados</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {TIME_SLOTS.map((slot) => {
                const booked = selectedDateBookings.filter(b => b.time_slot === slot);
                const isBooked = booked.length > 0;
                return (
                  <div key={slot} className={`border p-2 text-center text-sm ${isBooked ? "border-[#CCFF00]/30 bg-[#CCFF00]/10" : "border-white/10 bg-[#0A0A0A]"}`} data-testid={`slot-${slot}`}>
                    <span className={`font-['JetBrains_Mono'] font-bold ${isBooked ? "text-[#CCFF00]" : "text-gray-400"}`}>{slot}</span>
                    {isBooked && <p className="text-xs text-gray-500 mt-1">{booked.map(b => b.court_name).join(", ")}</p>}
                    {!isBooked && <p className="text-xs text-gray-600 mt-1">Disponible</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <>
          {rentals.length === 0 ? (
            <div className="card-tactical p-12 text-center"><CalendarCheck size={48} weight="duotone" className="text-gray-600 mx-auto mb-4" /><p className="text-gray-400">No hay reservas</p></div>
          ) : (
            <div className="space-y-3">
              {rentals.map((r) => {
                const club = clubs.find((c) => c.id === r.club_id);
                const isMine = r.user_id === user?.id;
                return (
                  <div key={r.id} className="card-tactical p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`rental-card-${r.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white">{r.court_name}</span>
                        <span className="text-xs text-gray-500">{club?.name}</span>
                      </div>
                      <div className="flex gap-4 text-xs text-gray-500 flex-wrap">
                        <span>📅 {r.date}</span><span>⏰ {r.time_slot}</span><span>{r.user_name}</span>
                      </div>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <Badge variant="outline" className={`badge-status ${statusColors[r.status]}`}>{r.status}</Badge>
                        <Badge variant="outline" className={`badge-status ${paymentColors[r.payment_status]}`}>{r.payment_status}</Badge>
                        <span className="text-[#CCFF00] font-['JetBrains_Mono'] text-sm font-bold">${r.amount} MXN</span>
                        <span className={`text-[10px] uppercase font-bold px-2 py-1 ${(r.players||[]).length === 4 ? "bg-[#CCFF00]/10 text-[#CCFF00] border border-[#CCFF00]/30" : "bg-yellow-500/10 text-yellow-400 border border-yellow-500/30"}`} data-testid={`rental-players-count-${r.id}`}>
                          {(r.players || []).length}/4 jugadores
                        </span>
                      </div>
                      {(r.players || []).length > 0 && (
                        <div className="flex gap-1.5 flex-wrap mt-2" data-testid={`rental-players-${r.id}`}>
                          {(r.players || []).map((p, i) => (
                            <span key={i} className={`px-2 py-0.5 text-[10px] ${p.is_guest ? "bg-yellow-500/10 text-yellow-300 border border-yellow-500/20" : "bg-white/5 text-white border border-white/10"}`}>
                              {p.name}{p.is_guest ? " (inv)" : ""}
                            </span>
                          ))}
                        </div>
                      )}
                      {r.transfer_info && (
                        <div className="mt-2 text-[10px] text-gray-500 font-mono whitespace-pre-wrap">{r.transfer_info}</div>
                      )}
                    </div>
                    {(isMine || isAdminOrDir) && (
                      <div className="flex gap-2 shrink-0 flex-wrap">
                        {r.status !== "cancelada" && (
                          <Button size="sm" variant="outline" onClick={() => openPlayersDialog(r)} className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 text-xs uppercase font-bold" data-testid={`rental-players-btn-${r.id}`}>
                            <UsersFour size={14} className="mr-1" /> Jugadores
                          </Button>
                        )}
                        {r.status === "reservada" && (r.players || []).length === 4 && (
                          <Button size="sm" onClick={() => confirmRental(r.id)} className="bg-green-500 hover:bg-green-600 text-white text-xs uppercase font-bold" data-testid={`rental-confirm-${r.id}`}>
                            <CheckCircle size={14} className="mr-1" /> Confirmar
                          </Button>
                        )}
                        {isAdminOrDir && r.status !== "cancelada" && (
                          <Button size="sm" variant="outline" onClick={() => openAdminEdit(r)} className="border-white/10 text-gray-300 hover:text-[#CCFF00] text-xs uppercase font-bold" data-testid={`rental-admin-edit-${r.id}`}>
                            <PencilSimple size={14} className="mr-1" /> Editar
                          </Button>
                        )}
                        {r.status !== "cancelada" && (r.players || []).length === 4 && (
                          <Button size="sm" onClick={() => launchMatchFromRental(r)} className="bg-[#CCFF00]/10 text-[#CCFF00] hover:bg-[#CCFF00]/20 border border-[#CCFF00]/30 text-xs uppercase font-bold" data-testid={`rental-scoreboard-${r.id}`}>
                            <Broadcast size={14} className="mr-1" /> Marcador
                          </Button>
                        )}
                        {isMine && r.payment_status === "pendiente" && r.status !== "cancelada" && (
                          <Button size="sm" onClick={() => handleUploadProof(r.id)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] text-xs uppercase font-bold" data-testid={`upload-proof-${r.id}`}>
                            <UploadSimple size={14} className="mr-1" /> Comprobante
                          </Button>
                        )}
                        {r.proof_path && (
                          <Button size="sm" variant="outline" onClick={() => viewProof(r.id)} className="border-white/20 text-gray-300 text-xs uppercase font-bold" data-testid={`view-proof-${r.id}`}>
                            <Eye size={14} className="mr-1" /> Ver
                          </Button>
                        )}
                        {(isMine || isAdminOrDir) && r.status !== "cancelada" && (
                          <Button size="sm" variant="outline" onClick={() => handleCancel(r.id)} className="border-white/20 text-gray-400 hover:text-red-400 text-xs uppercase font-bold" data-testid={`cancel-rental-${r.id}`}>
                            <X size={14} className="mr-1" /> Cancelar
                          </Button>
                        )}
                        <EventMediaGallery entityType="court_rentals" entityId={r.id} testIdPrefix={`rental-media-${r.id}`} />
                      </div>
                    )}
                    {user?.role === "admin" && !isMine && (
                      <div className="flex gap-2 shrink-0 flex-wrap">
                        {(r.payment_status === "pendiente" || r.payment_status === "en_revision") && r.status !== "cancelada" && (
                          <Button size="sm" onClick={() => handleConfirmPayment(r.id)} className="bg-green-600 hover:bg-green-700 text-white text-xs uppercase font-bold" data-testid={`confirm-pay-${r.id}`}>
                            <CheckCircle size={14} className="mr-1" /> Confirmar pago
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => handleAdminDelete(r.id)} className="border-white/20 text-gray-400 hover:text-red-400 text-xs uppercase font-bold" data-testid={`admin-delete-rental-${r.id}`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md" data-testid="rental-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Reservar Cancha</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Club</Label>
              <Select value={form.club_id} onValueChange={(v) => update("club_id", v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="rental-club-select"><SelectValue placeholder="Seleccionar club" /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">{clubs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {selectedClub && (
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Cancha</Label>
                <Select value={form.court_name} onValueChange={(v) => update("court_name", v)}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="rental-court-select"><SelectValue placeholder="Seleccionar cancha" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">{(
                    (selectedClub.court_names && selectedClub.court_names.length > 0)
                      ? selectedClub.court_names
                      : Array.from({ length: selectedClub.courts_count || 0 }, (_, i) => `Cancha ${i + 1}`)
                  ).map((cn) => <SelectItem key={cn} value={cn}>{cn}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Fecha</Label>
              <Input data-testid="rental-date-input" type="date" value={form.date} onChange={(e) => update("date", e.target.value)}
                className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" required />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Horario</Label>
              <Select value={form.time_slot} onValueChange={(v) => update("time_slot", v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="rental-time-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">{((selectedClub && selectedClub.time_slots && selectedClub.time_slots.length > 0) ? selectedClub.time_slots : TIME_SLOTS).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Precio:</span>
                <span className="text-[#CCFF00] font-['JetBrains_Mono'] font-bold">${((selectedClub && selectedClub.rental_prices && selectedClub.rental_prices["1h"]) || prices["1h"] || 25).toFixed(2)} MXN / hora</span>
              </div>
              <div className="border-t border-white/5 pt-2">
                <p className="text-xs text-gray-500 mb-1">Transferencia bancaria BBVA:</p>
                <p className="font-['JetBrains_Mono'] text-xs text-white">CLABE: 012180015254421972</p>
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <WhatsappLogo size={12} className="text-green-500" /> WhatsApp: 4641694920
                </p>
              </div>
            </div>
            <DialogFooter><Button type="submit" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide w-full" data-testid="rental-submit-btn">Reservar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Players Dialog (4 jugadores) */}
      <Dialog open={!!playersDialog} onOpenChange={(v) => !v && setPlayersDialog(null)}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rental-players-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Jugadores · {playersDialog?.court_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-400">Define exactamente 4 jugadores para confirmar la reserva. Puedes mezclar jugadores registrados con invitados.</p>
            {playersForm.map((p, i) => (
              <div key={i} className="bg-[#0A0A0A] border border-white/10 p-3">
                <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-2">Jugador {i + 1}</div>
                <SearchableUserSelect
                  value={p.user_id}
                  onChange={(id, pl) => setPlayersForm((prev) => prev.map((x, idx) => {
                    if (idx !== i) return x;
                    if (!id) return { user_id: "", name: x.name || "", is_guest: true };
                    return { user_id: id, name: pl?.name || "", is_guest: false };
                  }))}
                  options={allPlayers}
                  placeholder="— Invitado (nombre manual) —"
                  allowClear
                  testId={`rental-player-select-${i}`}
                  className="mb-2"
                />
                {(!p.user_id) && (
                  <Input
                    placeholder="Nombre del invitado"
                    value={p.name || ""}
                    onChange={(e) => setPlayersForm((prev) => prev.map((x, idx) => idx === i ? { ...x, name: e.target.value, is_guest: true, user_id: "" } : x))}
                    className="bg-[#141414] border-white/10"
                    data-testid={`rental-player-guest-${i}`}
                  />
                )}
              </div>
            ))}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPlayersDialog(null)} className="border-white/10">Cancelar</Button>
              <Button onClick={savePlayers} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="rental-save-players">Guardar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Edit Dialog (amount + transfer_info) */}
      <Dialog open={!!adminEditDialog} onOpenChange={(v) => !v && setAdminEditDialog(null)}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md" data-testid="rental-admin-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Editar reserva</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase">Fecha</Label>
                <Input type="date" value={adminEditForm.date} onChange={(e) => setAdminEditForm({ ...adminEditForm, date: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="rental-admin-date" />
              </div>
              <div>
                <Label className="text-xs uppercase">Horario</Label>
                <Input type="text" placeholder="08:00-09:30" value={adminEditForm.time_slot} onChange={(e) => setAdminEditForm({ ...adminEditForm, time_slot: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="rental-admin-time" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase">Cancha</Label>
              <Input value={adminEditForm.court_name} onChange={(e) => setAdminEditForm({ ...adminEditForm, court_name: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="rental-admin-court" />
            </div>
            <div>
              <Label className="text-xs uppercase">Cuota ($ MXN)</Label>
              <Input type="number" min="0" step="0.01" value={adminEditForm.amount} onChange={(e) => setAdminEditForm({ ...adminEditForm, amount: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="rental-admin-amount" />
            </div>
            <div>
              <Label className="text-xs uppercase">Datos de transferencia / nota</Label>
              <textarea
                value={adminEditForm.transfer_info}
                onChange={(e) => setAdminEditForm({ ...adminEditForm, transfer_info: e.target.value })}
                rows={4}
                className="w-full bg-[#0A0A0A] border border-white/10 p-2 text-sm font-mono"
                placeholder="CLABE / Banco / Referencia / WhatsApp"
                data-testid="rental-admin-transfer"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdminEditDialog(null)} className="border-white/10">Cancelar</Button>
              <Button onClick={saveAdminEdit} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="rental-admin-save">Guardar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
